import { supabase } from '../config/supabase';
import type { AppSettings, TimeFormat } from '../types';
import { formatEventTime, type EventClock } from '../../shared/eventTimezones';

const storageKey = 'courtcaptain:app-settings';

export const defaultAppSettings: AppSettings = {
  timeFormat: '12h',
};

export function validTimeFormat(value: unknown): TimeFormat {
  return value === '24h' ? '24h' : '12h';
}

export function toTimeInput(value: string | null | undefined): string {
  return value ? value.slice(0, 5) : '';
}

export function formatTime(value: string | null | undefined, timeFormat: TimeFormat, event?: EventClock): string {
  if (event) return formatEventTime(event, value, timeFormat);
  const time = toTimeInput(value);
  if (!time) return '';
  const [hoursRaw, minutesRaw] = time.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return time;
  if (timeFormat === '24h') return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

function loadLocalAppSettings(): AppSettings {
  try {
    const saved = window.localStorage.getItem(storageKey);
    if (!saved) return defaultAppSettings;
    const parsed = JSON.parse(saved) as Partial<AppSettings>;
    return { ...defaultAppSettings, timeFormat: validTimeFormat(parsed.timeFormat) };
  } catch {
    return defaultAppSettings;
  }
}

function saveLocalAppSettings(settings: AppSettings): void {
  window.localStorage.setItem(storageKey, JSON.stringify(settings));
}

export async function loadAppSettings(): Promise<AppSettings> {
  const localSettings = loadLocalAppSettings();
  const { data, error } = await supabase
    .from('app_settings')
    .select('setting_value')
    .eq('setting_key', 'time_format')
    .maybeSingle();

  if (error) return localSettings;

  const settings = { ...defaultAppSettings, timeFormat: validTimeFormat((data as { setting_value?: string } | null)?.setting_value) };
  saveLocalAppSettings(settings);
  return settings;
}

export async function saveAppSettings(settings: AppSettings): Promise<void> {
  saveLocalAppSettings(settings);
  const { error } = await supabase
    .from('app_settings')
    .upsert({ setting_key: 'time_format', setting_value: settings.timeFormat }, { onConflict: 'setting_key' });
  if (error) throw error;
}
